import { useState } from 'react';
import { contactAPI } from '../services/api';

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      // Save to database
      await contactAPI.submitForm(form);
      setSubmitted(true);
    } catch (err) {
      console.error('Error submitting form:', err);
      setError('Failed to submit form. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#0a0a0a] text-white min-h-screen pt-16">
      {/* Header */}
      <div className="relative bg-[#111111] border-b border-[#2a2a2a] py-16 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-green-500/5 rounded-full blur-3xl pointer-events-none" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <span className="text-green-500 text-xs font-bold uppercase tracking-widest">Get In Touch</span>
          <h1 className="text-4xl sm:text-5xl font-black mt-3 mb-4">
            Contact <span className="text-gradient">Us</span>
          </h1>
          <p className="text-gray-400 text-base leading-relaxed max-w-xl mx-auto">
            Want to host your Android app? Need support with your hosted apps? Have questions about our platform? We're here to help — reach out anytime.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
          {/* Contact Info */}
          <div className="lg:col-span-2 space-y-6">
            <div>
              <h2 className="text-xl font-bold mb-1">Reach Us Directly</h2>
              <p className="text-gray-500 text-sm leading-relaxed">
                We're available via WhatsApp, phone call, or the contact form. Response time is typically under 2 hours.
              </p>
            </div>

            {/* Cards */}
            {[
              {
                icon: '💬',
                title: 'WhatsApp (Primary)',
                lines: ['+254 793 973 389', '+254 717 325 657'],
                action: 'https://wa.me/254793973389?text=Hi,%20I%20want%20to%20host%20my%20Android%20app%20on%20SisaTeck',
                actionLabel: 'Chat on WhatsApp',
                green: true,
              },
              {
                icon: '📞',
                title: 'Phone Call',
                lines: ['+254 793 973 389', '+254 717 325 657'],
                action: 'tel:+254793973389',
                actionLabel: 'Call Now',
                green: false,
              },
              {
                icon: '⏰',
                title: 'Availability',
                lines: ['Mon – Sat: 8:00 AM – 9:00 PM', 'Sunday: 10:00 AM – 6:00 PM'],
                action: null,
                actionLabel: null,
                green: false,
              },
            ].map((card) => (
              <div
                key={card.title}
                className={`bg-[#1a1a1a] border rounded-2xl p-5 transition-all duration-200 ${card.green ? 'border-green-500/30 hover:border-green-500/50' : 'border-[#2a2a2a] hover:border-[#3a3a3a]'}`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center text-xl flex-shrink-0 ${card.green ? 'bg-green-500/15 border border-green-500/20' : 'bg-white/5 border border-white/10'}`}>
                    {card.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-semibold text-sm mb-1">{card.title}</h3>
                    {card.lines.map((line) => (
                      <p key={line} className="text-gray-400 text-sm">{line}</p>
                    ))}
                    {card.action && (
                      <a
                        href={card.action}
                        target={card.action.startsWith('http') ? '_blank' : undefined}
                        rel="noreferrer"
                        className={`inline-flex items-center gap-1 text-xs font-semibold mt-2 transition-colors ${card.green ? 'text-green-400 hover:text-green-300' : 'text-gray-400 hover:text-white'}`}
                      >
                        {card.actionLabel} →
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {/* Quick WhatsApp */}
            <a
              href="https://wa.me/254793973389?text=Hi,%20I%20want%20to%20host%20my%20Android%20app%20on%20SisaTeck"
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-center gap-2 w-full bg-green-500 hover:bg-green-400 text-black font-bold py-3.5 rounded-xl transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-green-500/30 text-sm"
            >
              💬 Contact Support on WhatsApp
            </a>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-3">
            <div className="bg-[#111111] border border-[#2a2a2a] rounded-2xl p-6 sm:p-8">
              {submitted ? (
                <div className="text-center py-12">
                  <div className="text-5xl mb-4">✅</div>
                  <h3 className="text-xl font-bold text-white mb-2">Message Sent Successfully!</h3>
                  <p className="text-gray-400 text-sm mb-6">
                    Your message has been sent to our admin team. We'll review it and respond soon.
                  </p>
                  <button
                    onClick={() => { setSubmitted(false); setForm({ name: '', email: '', phone: '', subject: '', message: '' }); }}
                    className="bg-green-500 hover:bg-green-400 text-black font-bold px-6 py-2.5 rounded-lg text-sm transition-colors"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <>
                  <h2 className="text-xl font-bold mb-1">Send a Message</h2>
                  <p className="text-gray-500 text-sm mb-6">Fill in the form and we'll respond via WhatsApp or call.</p>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Full Name *</label>
                        <input
                          type="text"
                          name="name"
                          value={form.name}
                          onChange={handleChange}
                          required
                          placeholder="John Doe"
                          className="w-full bg-[#1a1a1a] border border-[#2a2a2a] focus:border-green-500/50 focus:outline-none focus:ring-1 focus:ring-green-500/20 text-white placeholder-gray-600 rounded-xl px-4 py-3 text-sm transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Email Address</label>
                        <input
                          type="email"
                          name="email"
                          value={form.email}
                          onChange={handleChange}
                          placeholder="john@example.com"
                          className="w-full bg-[#1a1a1a] border border-[#2a2a2a] focus:border-green-500/50 focus:outline-none focus:ring-1 focus:ring-green-500/20 text-white placeholder-gray-600 rounded-xl px-4 py-3 text-sm transition-colors"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Phone Number</label>
                        <input
                          type="tel"
                          name="phone"
                          value={form.phone}
                          onChange={handleChange}
                          placeholder="+254 7XX XXX XXX"
                          className="w-full bg-[#1a1a1a] border border-[#2a2a2a] focus:border-green-500/50 focus:outline-none focus:ring-1 focus:ring-green-500/20 text-white placeholder-gray-600 rounded-xl px-4 py-3 text-sm transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Subject *</label>
                        <select
                          name="subject"
                          value={form.subject}
                          onChange={handleChange}
                          required
                          className="w-full bg-[#1a1a1a] border border-[#2a2a2a] focus:border-green-500/50 focus:outline-none focus:ring-1 focus:ring-green-500/20 text-white rounded-xl px-4 py-3 text-sm transition-colors appearance-none"
                        >
                          <option value="" className="bg-[#1a1a1a]">Select a subject</option>
                          <option value="Host My App" className="bg-[#1a1a1a]">Host My App</option>
                          <option value="Technical Support" className="bg-[#1a1a1a]">Technical Support</option>
                          <option value="Get Admin Access" className="bg-[#1a1a1a]">Get Admin Access</option>
                          <option value="Platform Features" className="bg-[#1a1a1a]">Platform Features</option>
                          <option value="General Inquiry" className="bg-[#1a1a1a]">General Inquiry</option>
                          <option value="Other" className="bg-[#1a1a1a]">Other</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Message *</label>
                      <textarea
                        name="message"
                        value={form.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        placeholder="Tell us how we can help you..."
                        className="w-full bg-[#1a1a1a] border border-[#2a2a2a] focus:border-green-500/50 focus:outline-none focus:ring-1 focus:ring-green-500/20 text-white placeholder-gray-600 rounded-xl px-4 py-3 text-sm transition-colors resize-none"
                      />
                    </div>

                    {error && (
                      <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-xl text-sm">
                        {error}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-green-500 hover:bg-green-400 disabled:bg-gray-600 disabled:cursor-not-allowed text-black font-bold py-3.5 rounded-xl transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-green-500/30 text-sm"
                    >
                      {submitting ? 'Submitting...' : 'Send Message →'}
                    </button>

                    <p className="text-gray-600 text-xs text-center">
                      Your message will be sent to our admin team. We'll respond via WhatsApp or call at{' '}
                      <a href="tel:+254793973389" className="text-green-500 hover:underline">+254 793 973 389</a>.
                    </p>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
